import { useMemo, useState } from "react";
import { ExternalLink, Trash2, Eye, Search, Inbox } from "lucide-react";
import { toast } from "sonner";
import { useApi, fmtBytes, fmtDate } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

export default function HistoryTable({ crawls, loading, onOpen, onChanged, scope = "mine", setScope, hasTeam, myId }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const api = useApi();

  const rows = useMemo(() => {
    const t = q.toLowerCase();
    return crawls.filter(
      (c) =>
        (status === "all" || c.status === status) &&
        `${c.case_caption} ${c.index_number} ${c.url} ${c.exported_by}`.toLowerCase().includes(t)
    );
  }, [crawls, q, status]);

  const remove = async (c) => {
    if (!window.confirm(`Delete history for "${c.case_caption}"?`)) return;
    try {
      await api.delete(`/crawls/${c.id}`);
      toast.success("Crawl removed from history");
      onChanged();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <section className="space-y-5" data-testid="history-section">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Crawl history</h2>
          <p className="text-sm text-slate-500 mt-1">{scope === "team" ? "Every case your team has exported. The extension also skips filings a teammate already saved." : "Every ZIP you make with the extension is saved here."}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasTeam && (
            <div className="inline-flex p-1 rounded-md bg-slate-100 border border-slate-200" data-testid="history-scope-toggle">
              {["mine", "team"].map((s) => (
                <button key={s} data-testid={`history-scope-${s}`} onClick={() => setScope(s)} className={`h-8 px-3 rounded text-sm font-semibold transition-colors ${scope === s ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
                  {s === "mine" ? "Mine" : "Whole team"}
                </button>
              ))}
            </div>
          )}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="history-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search caption or index #"
              className="h-10 pl-8 pr-3 w-full sm:w-64 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
            />
          </div>
          <select
            data-testid="history-status-filter"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
          >
            <option value="all">All</option>
            <option value="completed">Completed</option>
            <option value="partial">Partial</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="history-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left font-medium px-5 py-3">Case</th>
              {scope === "team" && <th className="text-left font-medium px-5 py-3">Exported by</th>}
              <th className="text-left font-medium px-5 py-3">Crawled</th>
              <th className="text-right font-medium px-5 py-3">Found</th>
              <th className="text-right font-medium px-5 py-3">Zipped</th>
              <th className="text-right font-medium px-5 py-3">Size</th>
              <th className="text-left font-medium px-5 py-3">Status</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((c) => (
              <tr key={c.id} data-testid={`crawl-row-${c.id}`} className="hover:bg-slate-50 transition-colors">
                <td className="px-5 py-4 max-w-sm">
                  <button onClick={() => onOpen(c)} className="text-left font-semibold hover:text-blue-600 line-clamp-2" data-testid={`crawl-open-${c.id}`}>
                    {c.case_caption}
                  </button>
                  {c.index_number && <div className="font-mono text-xs text-amber-700 mt-0.5">Index # {c.index_number}</div>}
                </td>
                {scope === "team" && <td className="px-5 py-4 text-slate-700 max-w-[200px] truncate" data-testid={`crawl-author-${c.id}`}>{c.user_id === myId ? "You" : c.exported_by}</td>}
                <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                <td className="px-5 py-4 text-right tabular-nums">{c.found_count}</td>
                <td className="px-5 py-4 text-right tabular-nums">{c.downloaded_count}/{c.selected_count}</td>
                <td className="px-5 py-4 text-right tabular-nums text-slate-600">{fmtBytes(c.total_bytes)}</td>
                <td className="px-5 py-4"><StatusBadge status={c.status} /></td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-1">
                    <IconBtn testid={`crawl-view-${c.id}`} title="View documents" onClick={() => onOpen(c)}><Eye size={15} /></IconBtn>
                    <a href={c.url} target="_blank" rel="noreferrer" title="Open on NYSCEF" data-testid={`crawl-nyscef-link-${c.id}`} className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-blue-600 transition-colors"><ExternalLink size={15} /></a>
                    {(!c.user_id || c.user_id === myId) && <IconBtn testid={`crawl-delete-${c.id}`} title="Delete" onClick={() => remove(c)} danger><Trash2 size={15} /></IconBtn>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && rows.length === 0 && (
          <div className="py-16 text-center text-slate-500" data-testid="history-empty">
            <Inbox className="mx-auto mb-3 text-slate-300" size={32} />
            {crawls.length ? "No crawls match your filters." : "No crawls yet. Install the extension and run it on a NYSCEF case page."}
          </div>
        )}
      </div>
    </section>
  );
}

const IconBtn = ({ children, onClick, title, testid, danger }) => (
  <button
    onClick={onClick}
    title={title}
    data-testid={testid}
    className={`p-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors ${danger ? "hover:text-red-600" : "hover:text-blue-600"}`}
  >
    {children}
  </button>
);
